import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../shared/contexts/auth-context';
import { isMasterUser, hasPermission, hasRentalAccess, hasSalesAccess, PERMISSIONS } from '../shared/utils/permissions';

const Sidebar = ({ isOpen = true, onToggle }) => {
    const location = useLocation();
    const auth = useAuth();
    const [isPropertyManagerOpen, setIsPropertyManagerOpen] = useState(true);
    const [isAdministrationOpen, setIsAdministrationOpen] = useState(true);
    const [isSalesPropertyManagerOpen, setIsSalesPropertyManagerOpen] = useState(true);

    const togglePropertyManager = () => {
        setIsPropertyManagerOpen(!isPropertyManagerOpen);
    };

    const toggleAdministration = () => {
        setIsAdministrationOpen(!isAdministrationOpen);
    };

    const toggleSalesPropertyManager = () => {
        setIsSalesPropertyManagerOpen(!isSalesPropertyManagerOpen);
    };

    const isActive = (path) => {
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    return (
        <>
            {/* Mobile backdrop */}
            {isOpen && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
                    onClick={onToggle}
                />
            )}
            
            {/* Sidebar */}
            <div className={`w-64 bg-gray-800 text-white h-screen fixed left-0 top-0 overflow-y-auto z-50 transform transition-transform duration-300 ease-in-out ${
                isOpen ? 'translate-x-0' : '-translate-x-full'
            } md:translate-x-0`}>
            {/* Logo/Brand */}
            <div className="p-4 border-b border-gray-700">
                <Link to="/" className="flex items-center space-x-2 text-xl font-bold">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
                    </svg>
                    <span>Property Manager</span>
                </Link>
            </div>

            {/* Navigation */}
            <nav className="p-4 flex-1">
                {/* Property Manager Feature */}
                {hasRentalAccess(auth.user) && <div className="mb-4">
                    <button
                        onClick={togglePropertyManager}
                        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-gray-700 rounded transition-colors"
                    >
                        <div className="flex items-center space-x-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                            </svg>
                            <span className="font-medium">Rental Property Manager</span>
                        </div>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className={`h-4 w-4 transition-transform ${isPropertyManagerOpen ? 'rotate-180' : ''}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>

                    {/* Property Manager Sub-items */}
                    {isPropertyManagerOpen && (
                        <div className="ml-4 mt-2 space-y-1">
                            <Link
                                to="/"
                                className={`flex items-center space-x-2 px-3 py-2 rounded transition-colors ${
                                    isActive('/') && location.pathname === '/' ? 'bg-blue-600 text-white' : 'hover:bg-gray-700'
                                }`}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"/>
                                </svg>
                                <span>Dashboard</span>
                            </Link>

                            {hasPermission(auth.user, PERMISSIONS.PROPERTIES) && (
                                <Link
                                    to="/properties"
                                    className={`flex items-center space-x-2 px-3 py-2 rounded transition-colors ${
                                        isActive('/properties') && !location.pathname.includes('/property-search') ? 'bg-blue-600 text-white' : 'hover:bg-gray-700'
                                    }`}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                                    </svg>
                                    <span>Properties</span>
                                </Link>
                            )}

                            {hasPermission(auth.user, PERMISSIONS.LANDLORDS) && (
                                <Link
                                    to="/landlords"
                                    className={`flex items-center space-x-2 px-3 py-2 rounded transition-colors ${
                                        isActive('/landlords') ? 'bg-blue-600 text-white' : 'hover:bg-gray-700'
                                    }`}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                                    </svg>
                                    <span>Landlords</span>
                                </Link>
                            )}

                            {hasPermission(auth.user, PERMISSIONS.TENANTS) && (
                                <Link
                                    to="/tenants"
                                    className={`flex items-center space-x-2 px-3 py-2 rounded transition-colors ${
                                        isActive('/tenants') ? 'bg-blue-600 text-white' : 'hover:bg-gray-700'
                                    }`}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                                    </svg>
                                    <span>Tenants</span>
                                </Link>
                            )}

                            {hasPermission(auth.user, PERMISSIONS.PROPERTY_SEARCH) && (
                                <Link
                                    to="/property-search"
                                    className={`flex items-center space-x-2 px-3 py-2 rounded transition-colors ${
                                        isActive('/property-search') ? 'bg-blue-600 text-white' : 'hover:bg-gray-700'
                                    }`}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                                    </svg>
                                    <span>Search</span>
                                </Link>
                            )}

                            <Link
                                to="/fica"
                                className={`flex items-center space-x-2 px-3 py-2 rounded transition-colors ${
                                    isActive('/fica') ? 'bg-blue-600 text-white' : 'hover:bg-gray-700'
                                }`}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                </svg>
                                <span>Fica</span>
                            </Link>

                        </div>
                    )}
                </div>}

                {/* Sales Property Manager Feature */}
                {hasSalesAccess(auth.user) && <div className="mb-4">
                    <button
                        onClick={toggleSalesPropertyManager}
                        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-gray-700 rounded transition-colors"
                    >
                        <div className="flex items-center space-x-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                            <span className="font-medium">Sales Property Manager</span>
                        </div>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className={`h-4 w-4 transition-transform ${isSalesPropertyManagerOpen ? 'rotate-180' : ''}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>

                    {/* Sales Property Manager Sub-items */}
                    {isSalesPropertyManagerOpen && (
                        <div className="ml-4 mt-2 space-y-1">

                            <Link
                                to="/sales/pipeline-dashboard"
                                className={`flex items-center space-x-2 px-3 py-2 rounded transition-colors ${
                                    isActive('/sales/pipeline-dashboard') ? 'bg-blue-600 text-white' : 'hover:bg-gray-700'
                                }`}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"/>
                                </svg>
                                <span>Pipeline Dashboard</span>
                            </Link>

                            <Link
                                to="/sales/deals"
                                className={`flex items-center space-x-2 px-3 py-2 rounded transition-colors ${
                                    isActive('/sales/deals') ? 'bg-blue-600 text-white' : 'hover:bg-gray-700'
                                }`}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
                                </svg>
                                <span>Sales Deals</span>
                            </Link>

                            <Link
                                to="/sales/professional-services"
                                className={`flex items-center space-x-2 px-3 py-2 rounded transition-colors ${
                                    isActive('/sales/professional-services') ? 'bg-blue-600 text-white' : 'hover:bg-gray-700'
                                }`}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                                </svg>
                                <span>Professional Services</span>
                            </Link>

                            <Link
                                to="/sales/commission-statements"
                                className={`flex items-center space-x-2 px-3 py-2 rounded transition-colors ${
                                    isActive('/sales/commission-statements') ? 'bg-blue-600 text-white' : 'hover:bg-gray-700'
                                }`}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
                                </svg>
                                <span>Commission Statements</span>
                            </Link>

                            <Link
                                to="/fica"
                                className={`flex items-center space-x-2 px-3 py-2 rounded transition-colors ${
                                    isActive('/fica') ? 'bg-blue-600 text-white' : 'hover:bg-gray-700'
                                }`}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                </svg>
                                <span>Fica</span>
                            </Link>
                        </div>
                    )}
                </div>}

                {/* Administration Feature */}
                <div className="mb-4">
                    <button
                        onClick={toggleAdministration}
                        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-gray-700 rounded transition-colors"
                    >
                        <div className="flex items-center space-x-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                            </svg>
                            <span className="font-medium">Administration</span>
                        </div>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className={`h-4 w-4 transition-transform ${isAdministrationOpen ? 'rotate-180' : ''}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>

                    {isAdministrationOpen && (
                        <div className="ml-4 mt-2 space-y-1">
                            {hasPermission(auth.user, PERMISSIONS.AGENTS) && (
                                <Link
                                    to="/agents"
                                    className={`flex items-center space-x-2 px-3 py-2 rounded transition-colors ${
                                        isActive('/agents') ? 'bg-blue-600 text-white' : 'hover:bg-gray-700'
                                    }`}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                                    </svg>
                                    <span>Agents</span>
                                </Link>
                            )}

                            {isMasterUser(auth.user) && (
                                <Link
                                    to="/user-management"
                                    className={`flex items-center space-x-2 px-3 py-2 rounded transition-colors ${
                                        isActive('/user-management') ? 'bg-blue-600 text-white' : 'hover:bg-gray-700'
                                    }`}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"/>
                                    </svg>
                                    <span>User Management</span>
                                </Link>
                            )}
                        </div>
                    )}
                </div>
            </nav>

            {/* Logout Button - Fixed at bottom */}
            <div className="p-4 border-t border-gray-700">
                <button
                    onClick={() => auth.logout()}
                    className="w-full flex items-center space-x-2 px-3 py-2 text-left hover:bg-gray-700 rounded transition-colors text-red-400 hover:text-red-300"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span>Logout</span>
                </button>
            </div>
        </div>
        </>
    );
};

export default Sidebar;